import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, BarChart, Cell } from "recharts";
import { TrendingUp, Clock, AlertTriangle, Target, Receipt } from "lucide-react";

const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const AGING = [
  { key: "current", label: "Not yet due", color: "#16a34a" },
  { key: "1_30", label: "1-30 days", color: "#eab308" },
  { key: "31_60", label: "31-60 days", color: "#f97316" },
  { key: "61_90", label: "61-90 days", color: "#ef4444" },
  { key: "90_plus", label: "90+ days", color: "#7f1d1d" },
];

const Kpi = ({ label, value, sub, icon: Icon, cls, testId }) => (
  <Card className="bg-white border-0 shadow">
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
        {Icon && <Icon className={`w-4 h-4 ${cls}`} />}
      </div>
      <p className={`text-2xl font-bold ${cls}`} data-testid={testId}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </CardContent>
  </Card>
);

const Panel = ({ title, children, testId }) => (
  <Card className="bg-white border-0 shadow-lg" data-testid={testId}>
    <CardContent className="p-5">
      <h3 className="font-semibold text-slate-800 mb-4">{title}</h3>
      {children}
    </CardContent>
  </Card>
);

export const BusinessHealthReport = ({ data }) => {
  if (!data) return null;
  const { kpis: k, monthly, aging, top_customers, technicians, quotes, payments_by_method = [] } = data;
  const METHOD = { cash_app: "Cash App", zelle: "Zelle", stripe: "Stripe / Card", check: "Check", cash: "Cash", other: "Other" };
  const agingRows = AGING.map((a) => ({ ...a, amount: aging.amounts[a.key], count: aging.counts[a.key] }));

  return (
    <div className="space-y-6" data-testid="business-health-report">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Billed" value={money(k.billed)} sub={`${k.invoice_count} invoices · avg ${money(k.avg_invoice)}`} icon={TrendingUp} cls="text-blue-600" testId="bh-billed" />
        <Kpi label="Collected" value={money(k.collected)} sub={`${k.collection_rate_pct}% collection rate · ${k.payment_count ?? 0} payments`} cls="text-green-600" testId="bh-collected" />
        {data.expenses_total !== undefined && <Kpi label="Expenses" value={money(data.expenses_total)} sub={`Net profit ${money(data.net_profit)}${data.mileage_deduction ? ` · mileage ${money(data.mileage_deduction)} (${data.mileage_miles} mi)` : ""}`} icon={Receipt} cls={data.net_profit >= 0 ? "text-emerald-700" : "text-red-600"} testId="bh-expenses" />}
        <Kpi label="Outstanding" value={money(k.outstanding)} sub={`${money(k.overdue)} overdue`} icon={AlertTriangle} cls="text-orange-600" testId="bh-outstanding" />
        <Kpi label="Hours" value={`${k.hours_logged.toFixed(1)} h`} sub={`${k.hours_invoiced.toFixed(1)} h invoiced · ${k.open_work_entries} open entries`} icon={Clock} cls="text-indigo-600" testId="bh-hours" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <Panel title="Monthly Revenue — Billed vs Collected" testId="bh-monthly">
            {monthly.length === 0 ? <p className="text-sm text-slate-400">No invoices in this period</p> : (
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={monthly} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
                  <Tooltip formatter={(v) => money(v)} />
                  <Legend />
                  <Bar dataKey="billed" name="Billed" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="collected" name="Collected" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  <Line type="monotone" dataKey="outstanding" name="Outstanding" stroke="#f97316" strokeWidth={2} dot />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </div>
        <Panel title="Receivables Aging" testId="bh-aging">
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={agingRows} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} />
              <YAxis hide />
              <Tooltip formatter={(v) => money(v)} />
              <Bar dataKey="amount" name="Amount" radius={[4, 4, 0, 0]}>
                {agingRows.map((r) => <Cell key={r.key} fill={r.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-2 space-y-1">
            {agingRows.map((r) => (
              <div key={r.key} className="flex items-center justify-between text-sm" data-testid={`bh-aging-${r.key}`}>
                <span className="flex items-center gap-2 text-slate-600"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: r.color }} />{r.label} <span className="text-slate-400">({r.count})</span></span>
                <span className="font-semibold text-slate-800">{money(r.amount)}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Panel title="Top Customers by Billing" testId="bh-top-customers">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Customer</TableHead><TableHead className="text-right">Invoices</TableHead><TableHead className="text-right">Billed</TableHead><TableHead className="text-right">Outstanding</TableHead></TableRow></TableHeader>
            <TableBody>
              {top_customers.map((c) => (
                <TableRow key={c.customer_id}><TableCell className="font-medium text-slate-800">{c.customer_name}</TableCell><TableCell className="text-right">{c.invoices}</TableCell><TableCell className="text-right font-semibold text-blue-600">{money(c.billed)}</TableCell><TableCell className="text-right text-orange-600">{money(c.outstanding)}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        </Panel>
        <Panel title="Technician Productivity" testId="bh-technicians">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Technician</TableHead><TableHead className="text-right">Logged</TableHead><TableHead className="text-right">Invoiced</TableHead><TableHead className="text-right">Utilization</TableHead><TableHead className="text-right">Billed</TableHead></TableRow></TableHeader>
            <TableBody>
              {technicians.map((t) => (
                <TableRow key={t.technician_id}>
                  <TableCell className="font-medium text-slate-800">{t.technician_name}</TableCell>
                  <TableCell className="text-right">{t.hours_logged.toFixed(1)} h</TableCell>
                  <TableCell className="text-right">{t.hours_invoiced.toFixed(1)} h</TableCell>
                  <TableCell className="text-right">
                    <span className="inline-flex items-center gap-2"><span className="w-16 h-1.5 rounded bg-slate-200 overflow-hidden"><span className="block h-full bg-indigo-500" style={{ width: `${Math.min(100, t.utilization_pct)}%` }} /></span>{t.utilization_pct}%</span>
                  </TableCell>
                  <TableCell className="text-right font-semibold text-blue-600">{money(t.billed)}<p className="text-xs text-slate-400 font-normal">{t.effective_rate ? `${money(t.effective_rate)}/h` : ""}</p></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Panel>
      </div>

      <Panel title="Payments Received by Method" testId="bh-payments">
        {payments_by_method.length === 0 ? <p className="text-sm text-slate-400">No payments recorded in this period</p> : (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {payments_by_method.map((m) => (
              <div key={m.method} className="rounded-lg bg-slate-50 p-3" data-testid={`bh-method-${m.method}`}>
                <p className="text-xs uppercase tracking-wide text-slate-500">{METHOD[m.method] || m.method}</p>
                <p className="text-lg font-bold text-green-700">{money(m.amount)}</p>
                <p className="text-xs text-slate-500">{m.count} payment{m.count === 1 ? "" : "s"}</p>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel title="Quote Pipeline" testId="bh-quotes">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {["draft", "pending_approval", "approved", "rejected"].map((st) => (
            <div key={st} className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">{st.replace("_", " ")}</p>
              <p className="text-lg font-bold text-slate-800">{quotes.by_status[st] || 0}</p>
              <p className="text-xs text-slate-500">{money(quotes.value_by_status[st])}</p>
            </div>
          ))}
          <div className="rounded-lg bg-indigo-50 p-3">
            <p className="text-xs uppercase tracking-wide text-indigo-600 flex items-center gap-1"><Target className="w-3 h-3" /> Win rate</p>
            <p className="text-lg font-bold text-indigo-700" data-testid="bh-win-rate">{quotes.win_rate_pct == null ? "n/a" : `${quotes.win_rate_pct}%`}</p>
            <p className="text-xs text-indigo-600">{quotes.total} quotes · {money(quotes.total_value)}</p>
          </div>
        </div>
      </Panel>
    </div>
  );
};
